import React from 'react';

import { Link, Paragraph, Strong, Text } from '@constellation/core';
import * as routes from 'client/routes/manifest';
import { AppContent } from 'content/AppContent';

import { MetaContentValues } from '../MetaContentValues';

const limitedCompanyCriteria: AppContent['limitedCompanyCriteria'] = {
  metaContent: MetaContentValues.LimitedCompanyCriteria,
  pageTitle: 'Limited company criteria',
  introText:
    "Here's an overview of eligibility criteria for Limited Company buy to let mortgages. We've broken it down into easy-to-read sections for you. Just select them individually to find out what you need to know.",
  jumpToASection: {
    title: 'Jump to a section',
    id: 'jump-to-a-section',
    links: [
      { label: 'Company', url: 'company' },
      { label: 'Application', url: 'application' },
      { label: 'Customer', url: 'customer' },
      { label: 'Income', url: 'income' },
      { label: 'Lending', url: 'lending' },
      { label: 'Property', url: 'property' },
      { label: 'Existing customer', url: 'existing-customer' },
    ],
  },
  backToTopLabel: 'Back to top',
  company: {
    id: 'company',
    title: 'Company',
    companyStructure: {
      id: 'company-structure',
      title: 'Company Structure',
      listItems: [
        'We will accept recently formed Limited companies, they must be registered on Companies house before an application is made.',
        <>
          <Paragraph marginBottom="02">
            Special purpose vehicles are accepted with the following SIC codes:
          </Paragraph>
          <Paragraph marginTop="02" marginBottom="04">
            68209, 68100, 68320, 68201
          </Paragraph>
        </>,
        'Trading or layered companies are not accepted.',
        'Partnerships and LLPs are not accepted.',
        'Floating charges or debentures can not be in place over the company.',
        'Companies must be registered in England, Wales or Scotland.',
      ],
    },
    directorsShareholders: {
      id: 'directors-shareholders',
      title: 'Directors/Shareholders',
      listItems: [
        'Maximum of 4 parties to the company who must own 100% of the company between them.',
        'More than one Ltd Company can be financed with BM.',
        'Directors/Shareholders must reside in England, Wales or Scotland.',
        'All directors and shareholders will be guarantor for the mortgage.',
        'Personal Guarantees for the whole loan amount will be taken from all directors/shareholders.',
        'Independent Legal Advice will be required for each Personal Guarantee.',
        'All other standard applicant criteria applies e.g. minimum age, income requirements and property criteria.',
        'A soft credit search will apply for all directors/shareholders.',
        'The company must seek approval from us ahead making of any changes to the company structure/director/shareholders details.',
        'If there is limited experience of managing BTL property the application will be referred for an underwriter assessment.',
      ],
    },
    applicationCriteria: {
      id: 'application-criteria',
      title: 'Application Criteria',
      listItems: [
        'Purchase & re-mortgage.',
        '125% rental cover ratio applied to all Limited company applications.',
        'Max LTV 75%.',
        'Application property must be in England, Wales or Scotland.',
        'Further Advances are not available currently.',
        'Product Transfers will be available when a Limited company product ends.',
        'Purchase of directors/shareholders property is acceptable, they must have an onwards residential purchase and leave the property on completion. Anew residential mortgage offer/conveyancers letter will be required.',
        'Directors loan deposits are accepted.',
        'Must have a Limited company bank account for direct debit and fee payments, which must match the company name.',
        'Top Slicing is not available.',
        <Text>
          Standard Portfolio landlord criteria applies,{' '}
          <Link
            href="/ltd-co-criteria#portfolio-landlords"
            data-testid="application-criteria-portfolio-landlords"
          >
            Portfolio landlords
          </Link>
          .
        </Text>,
      ],
    },
    tenancyCriteria: {
      id: 'tenancy-criteria',
      title: 'Tenancy Criteria',
      listItems: ['Standard tenancy criteria applies.'],
    },
    sourceOfDeposit: {
      id: 'source-of-deposit',
      title: 'Source of Deposit',
      listItems: [
        'Directors loans are acceptable.',
        'Intercompany loans are not acceptable.',
        'Gift of equity accepted when the property is being purchased from director or shareholder.',
      ],
    },
  },
  application: {
    id: 'application',
    title: 'Application',
    directorsAndShareholders: {
      id: 'directors-and-shareholders',
      title: 'Directors and shareholders',
      paragraphs: [
        'The maximum number of directors and shareholders allowed for each application is four.',
        'If there are more than four directors and/or shareholders an application cannot be submitted.',
      ],
      link: {
        label: 'First-time buyer/first-time landlord',
        url: '/ltd-co-criteria#first-time-buyer-first-time-landlord',
      },
    },
    decisionInPrinciple: {
      id: 'decision-in-principle',
      title: 'Decision in Principle',
      paragraph1:
        'Our Decision in Principle provides a credit decision using minimal information. It states how much we can lend and is valid for three months, subject to a valuation and the information supplied being correct.',
    },
    directDebitDetails: {
      id: 'direct-debit-details',
      title: 'Direct Debit details',
      paragraph1:
        'We will need the details of the Limited company current account where the mortgage will be paid from at application stage. Only current accounts in the Limited company name will be accepted.',
    },
  },
  customer: {
    id: 'customer',
    title: 'Customer',
    idAddressVerification: {
      id: 'id-address-verification',
      title: 'ID & address verification',
      paragraphs: [
        'All directors and shareholders must be fully and properly identified as part of the mortgage application process.',
        'Accurate information must be recorded on our application system, including confirmation of whether this is a face-to-face or non-face-to-face application.',
        "If the director or shareholder doesn't pass our electronic verification checks, you'll need to certify and retain evidence of the identification documents.",
        "The documents you can accept depend on whether you've met with the directors and shareholders in person, or remotely by telephone or video.",
        "Here's a guide to understanding what documents you'll need.",
      ],
      singleIDV: {
        id: 'single-idv',
        title: 'Single ID&V',
        paragraphs: [
          'For face-to-face verification, where the directors and shareholders are physically present and documentation is taken and verified, one document from the single identification list can be used to verify the directors and shareholders.',
          "Single ID can't be used if the directors and shareholders aren't present when ID&V is collected. This includes video interviews. In this case, use the standard ID&V process.",
        ],
      },
      singleIDVDocuments: {
        id: 'single-idv-documents',
        title: 'Single ID&V documents:',
        listItems: [
          'UK photo card driving licence, showing the directors and shareholders current address.',
          'UK passport.',
          'Some EU/EEA identity cards.',
          'Some non-UK passports.',
        ],
        paragraph:
          'These documents can be accepted in a former name with evidence of the name change, such as a marriage certificate, Deed Poll or Decree Absolute.',
      },
      standardIDV: {
        id: 'standard-idv',
        title: 'Standard ID&V',
        paragraphs: [
          "Standard ID&V must be followed when the directors and shareholders aren't present when ID&V is obtained, or the directors/shareholders doesn't have a single ID document.",
          'The directors and shareholders will need to produce two separate documents. These must be originals and can only be used once in the process for either address verification or identification.',
        ],
        boldParagraph:
          'FCA registered firms must make sure any documentation relied upon to verify the directors and shareholders identity is copied, certified, and retained in accordance with money laundering regulations. We reserve the right to contact you and request copies of identity documents at any time during the life of the mortgage and for up to five years after cessation.',
        table: {
          headings: ['Standard ID&V Documents', 'Identification', 'Address'],
          rows: [
            [
              'UK photocard driving licence displaying the directors or shareholders current address',
              'Yes',
              'Yes',
            ],
            [
              'UK paper full driving licence (issued up to 30th June 1998)',
              'Yes',
              'Yes',
            ],
            ['Non-UK passports', 'Yes', 'No'],
            ['EU/EEA identity cards', 'Yes', 'No'],
            ['Biometric residence permit', 'Yes', 'No'],
            ['EU/EEA photo card driving licence', 'Yes', 'Yes'],
            [
              'UK Benefits/State Pension notification letter - dated within last 12 months',
              'Yes',
              'Yes',
            ],
            [
              'HMRC correspondence (excluding P45/P60) - must show Tax reference or NI number dated within last 12 months',
              'Yes',
              'Yes',
            ],
            [
              'Bank, building society or credit union statement dated within last 6 months and issued by a regulated financial sector firm in the UK',
              'No',
              'Yes',
            ],
            [
              'Mortgage statement from a bank or building society dated within last 12 months and issued by a regulated financial sector firm in the UK',
              'No',
              'Yes',
            ],
            [
              'Council Tax correspondence (excluding arrears and reminders) - Dated within last 12 months',
              'No',
              'Yes',
            ],
            ['Utility bills dated within last 6 months', 'No', 'Yes'],
            [
              "Solicitor's correspondence relating to house purchase and dated within last 3 months",
              'No',
              'Yes',
            ],
          ],
        },
      },
    },
    expatriates: {
      id: 'expatriates',
      title: 'Expatriates',
      paragraph: "We don't accept applications from Expatriates.",
    },
    foreignNationals: {
      id: 'foreign-nationals',
      title: 'Foreign nationals',
      paragraph1:
        'All applications from non-UK, non-Irish, nationals, EEA and non-EEA directors and stakeholders- proof of permanent right to reside will be required for all directors and shareholders on applications which do not meet the following criteria:',
      listItems: [
        'The directors or shareholders (or at least one director or stakeholder on a joint or multiple application) have lived continuously in the UK for more than 5 years.',
        'To determine if directors or shareholders have lived in the UK for more than five years we will use data provided by credit reference agencies.',
      ],
      paragraph2:
        "We'll accept indefinite leave to remain/Right of Abode, UK Residency stamps in non-UK passports, biometric residence permits, and Home Office documentation confirming indefinite leave to remain in the UK.",
      paragraph3:
        "For EEA/Swiss nationals with Settled or Pre settled status, the 'Share code' can used as residence.",
      refugeesLink: {
        label: 'Refugees',
        url: '/ltd-co-criteria#refugees',
      },
    },
    howToCertifyADocument: {
      id: 'how-to-certify-a-document',
      title: 'How to certify a document',
      paragraph1:
        'Only identification and address verification and right to reside documents need to be certified.',
      paragraph2:
        "You must certify the documents when they're collected and keep copies on file. You don't need to upload copies to the application unless we ask you to.",
      certifyingDocuments: {
        id: 'how-to-certify-a-document-certifying-documents',
        title: 'Certifying documents',
        paragraph1:
          'Documents must be certified by an FCA-registered mortgage intermediary. Certification should include:',
        listItems: [
          "Certifier's signature, name, and job title.",
          'The date of certification, which must be within three months of the application date.',
        ],
        paragraph2:
          'The certifier must also add written confirmation on the document. Suggested wording for this is:',
        paragraph3:
          "'I certify this is a true and correct copy of the original document as sighted by me'. You can use your own variation of this if you prefer.",
        paragraph4:
          "Separate 'certificates' are acceptable. These are sometimes called ID verification header sheets, broker/adviser verification checklists or similar.",
        listItems2: [
          'Details on the certificate must match those of the ID&V documents provided.',
          'Wording to confirm true and correct copies of documents should be included.',
          'The intermediary name and signature must be included on the certificate/document and the date must be within three months of the application date.',
        ],
      },
    },
    refugees: {
      id: 'refugees',
      title: 'Refugees',
      paragraph1:
        "Refugees and asylum seekers must have indefinite leave to remain. This will usually be given in a letter confirming they've been granted 'Indefinite Leave to Remain' from the Immigration and Nationality Directorate.",
      wecantaccept: {
        id: 'we-cant-accept-these-docs',
        title: "We can't accept these documents for mortgage purposes:",
        listItems: [
          'Exceptional leave to remain in the UK.',
          'Humanitarian protection.',
          'Discretionary leave.',
        ],
      },
    },
  },
  income: {
    id: 'income',
    title: 'Income',
    affordabilityAndSustainability: {
      id: 'affordability-and-sustainability',
      title: 'Affordability and sustainability',
      paragraphs: [
        "Affordability is assessed using the anticipated rental income from the property. We'll also assess personal income when making a lending decision, so it's important the directors and shareholders income is keyed accurately as it might affect the decision.",
        "When doing this, we expect to see the directors and shareholders can afford their personal commitments, so we can be confident our lending decision is sustainable if the tenant stops paying or the property needs any maintenance or repairs. An application might be declined if our assessment shows there's not enough disposable income to support the borrowing and the directors and shareholders personal commitments.",
        'In our rental cover calculation, we want the directors and shareholders to have more rent coming in than the normal mortgage payment, so the situations mentioned earlier can be covered.',
        "We'll also take the additional cost of increased tax liability for directors or shareholders into account.",
        'We understand personal income might not fully reflect the overall financial picture for the directors and shareholders. They could have other assets and savings, for example. We also know the buy to let might be in a single name, but personal financial commitments could be held jointly with someone else.',
      ],
      someInfoWeMightNeed: {
        id: 'some-info-we-might-need',
        title:
          'Some of the information we might need to manually underwrite an application, includes:',
        listItems: [
          'How the directors and shareholders manage their commitments.',
          "How they've built up any reserves, including the deposit.",
          'Whether they have previous experience of buying to let.',
          'The rationale for sole director, where finances are joint.',
          'Business details for self-employed directors and shareholders.',
        ],
      },
      learnMoreAbout: {
        id: 'learn-more-about',
        title: 'Learn more about:',
        links: [
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
          {
            label: 'Income types',
            url: '/ltd-co-criteria#income-types',
          },
        ],
      },
    },
    directorsOrShareholdersNotPaidInSterling: {
      id: 'directors-or-shareholders-not-paid-in-sterling',
      title: 'Directors or shareholders not paid in sterling',
      paragraphs: [
        'Foreign currency income is any income other than sterling received by directors or shareholders. Foreign currency income paid into a UK bank account and transferred to sterling is still considered to be foreign currency.',
        "Foreign currency income won't be accepted for new mortgage or remortgage applications for new and existing directors or shareholders, including porting, transfers of mortgaged property (TOMP), and further advances.",
        'The only exceptions to this are further advance applications for essential repairs to the mortgaged property and product transfers.',
      ],
    },
    contractors: {
      id: 'contractors',
      title: 'Contractors',
      paragraphs: [
        "If your directors or shareholders are classed as employed, they'll need to have been in their current role for at least three months.",
        "If your directors or shareholders are classed as self-employed, we'll need at least two years' worth of trading figures.",
      ],
      subcontractors: {
        id: 'subcontractors',
        title:
          'Sub-contractors, fixed/short term contracts, and agency workers',
        paragraphs: [
          "We'll consider applications from these directors and shareholders if they have continuous employment of 12 months or more with six months of the contract remaining, or the directors and shareholders have two years' continuous service in the same type of employment. Evidence of their employment record might be needed.",
        ],
      },
      itAndOtherContractors: {
        id: 'it-and-other-contractors',
        title: 'IT and other contractors',
        paragraphs: [
          'For IT contractors on any income and other contractors whose income is more than £500 per day or £75,000 per annum, whether employed or self-employed, we accept the gross value of the contract as evidence of income. All directors and shareholders must have a current continuous employment of 12 months or more with six months of the contract remaining or two years continuous service in the same type of employment.',
          "The directors and shareholders should be treated as self-employed if they have more than one contract, or where they've set up a limited company and employ other contractors.",
        ],
      },
      constructionIndustryScheme: {
        id: 'construction-industry-scheme',
        title: 'Construction Industry Scheme Contractors (CIS)',
        paragraphs: [
          "For these directors and shareholders, they'll need to provide an average of their latest 3 months income. They should be treated as self-employed if they pay their own tax or sub-contract to more than one company.",
        ],
      },
      zeroHoursContracts: {
        id: 'zero-hours-contracts',
        title: 'Zero-hours contracts',
        paragraphs: [
          "Applications from directors and shareholders employed on a zero-hours contract will be considered. We'll only take income from the total of the last 12 calendar months into account income. Directors and shareholders will have to verify their income.",
        ],
      },
      professionalSportsPeople: {
        id: 'professional-sports-people',
        title: 'Professional sports people',
        paragraphs: [
          "Professional sports people will have to confirm they've either been in continuous employment for 12 months or more with six months of the contract remaining, or two years' continuous service as at the date of application in the same type of employment.",
          "It's essential to verify these directors and shareholders will be able to meet their monthly repayments as they near the end of their career or if their career's ended early because of injury.",
        ],
      },
      additionalInformation: {
        id: 'contractors-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Length of employment/self-employment',
            url: '/ltd-co-criteria#length-of-employment-self-employment',
          },
          {
            label: 'Income types',
            url: '/ltd-co-criteria#income-types',
          },
          {
            label: 'Self-employed directors and share holders',
            url: '/ltd-co-criteria#self-employed-directors-and-shareholders',
          },
        ],
      },
    },
    incomeTypes: {
      id: 'income-types',
      title: 'Income types',
      paragraph1:
        'For all applications, at least one of the directors or shareholders needs to have an earned income and/or pension income. All income sources must be recorded accurately. This includes all full years of self-employed income, PAYE, pension income, and all gross monthly rental. All income sources need to be within the UK.',
      listItems: [
        "There's no minimum income level.",
        'Directors or shareholders must be registered for UK tax.',
        'Any residential mortgages must be covered by directors and shareholders income.',
        'Payslips must show the directors and shareholders and employer name, pay date, basic income, gross and net pay, and any additional payments being used in affordability assessments.',
        "We'll request an employer's reference where the directors and shareholders can't supply our preferred documentation. This doesn't apply where the directors and shareholders employed in a family business.",
        'Hand-written payslips are only acceptable alongside bank statements to confirm the income paid by the employer.',
        'Bank statements must show the directors and shareholders full name or initial, surname, and account number.',
        'Internet bank statements must show bank heading/name and http address.',
      ],
      table: {
        headings: [
          'Income Type',
          'Acceptable?',
          'Main or Other',
          'Key as',
          'Evidence',
        ],
        rows: [
          [
            'Additional Duty Hours (including Additional Responsibility Hours)',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['Adoption Allowance', 'No', '', '', ''],
          [
            'AFC Absence',
            'Yes',
            'Other',
            'Shift Allowance',
            'Latest 3 months payslips',
          ],
          ['Airbnb', 'No', '', '', ''],
          [
            'Area Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslip(s)',
          ],
          [
            'Armed Forces Independence Payments (AFIP)',
            'Yes',
            'Other',
            'Pension - Private',
            'Latest Bank Statement (where paid gross) or Latest Pension Statement / Latest months payslip(s) or Pension P60',
          ],
          [
            'Attendance Allowance (State Benefit)',
            'Yes',
            'Other',
            'Attendance Allowance',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          ['Attendance Allowance (Turning Up To Work)', 'No', '', '', ''],
          [
            "Bank Holiday Pay (only if it forms part of basic pay and doesn't inflate income)",
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Basic Salary (including the probationary period of a permanent contract)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Bereavement Allowance (previously Widows Pension. If State Widows Pension, please see below).',
            'No',
            '',
            '',
            '',
          ],
          [
            'Bonus (These must not be keyed unless they have actually been paid)',
            'Yes',
            'Main',
            'Annual Guaranteed Bonus',
            "Latest 3 months' payslips (or for incomes received less frequently than monthly all payslips showing this income for the latest 2 years)",
          ],
          ['Bounty Payment', 'No', '', '', ''],
          ['Bursary', 'No', '', '', ''],
          [
            'Call Out',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Car Allowance inc. Car Trade Down.',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Carers Allowance',
            'Yes',
            'Other',
            'Carers Allowance',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          [
            'Cash In Hand',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslip(s) and Bank Statement',
          ],
          [
            'CEA (Doctors Excellence Award)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Child Benefit',
            'Yes',
            'Other',
            'Child Benefit',
            'Latest Bank statement or Benefit Award Letter',
          ],
          [
            'Child Tax Credit',
            'Yes',
            'Other',
            'Child Tax Credit',
            'Latest Bank statement or Benefit Award Letter',
          ],
          [
            'Commission',
            'Yes',
            'Main',
            'Annual Regular / Guaranteed Commission',
            "Latest 3 months' payslips (or for incomes received less frequently than monthly all payslips showing this income for the latest 2 years)",
          ],
          [
            'Competency Related Threshold Payment',
            'Yes',
            'Main',
            'Annual Guaranteed Bonus',
            "Latest 3 months' payslips (or for incomes received less frequently than monthly all payslips showing this income for the latest 2 years)",
          ],
          [
            'Constant Attendance Allowance',
            'Yes',
            'Other',
            'Constant Attendance Allowance',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          [
            'Continual Professional Development (CPD)',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['Council Tax Benefit', 'No', '', '', ''],
          [
            'Country Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Danger Allowance',
            'Yes',
            'Main',
            'Annual Regular Overtime',
            'Latest 3 months payslips',
          ],
          [
            'Delivery Supplement',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Disability Living Allowance - not acceptable if received for a 3rd party e.g. dependent',
            'Yes',
            'Other',
            'Disability Living',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          [
            'Dividends - For Company Director',
            'Yes',
            'Main',
            'Self Employed',
            'See Limited Company Director/Shareholder under Self Employed/Contractors section',
          ],
          ['Dividends - Investment Linked', 'No', '', '', ''],
          [
            'Employed in a Family Business: If less than 25% invested interest and does not receive dividends or net profit as part of their reward package (Treat as employed)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslip(s), and corresponding bank statement(s)',
          ],
          [
            'Employment & Support Allowance (ESA)',
            'Yes',
            'Other',
            'Employment and Support Allowance',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          [
            'Examiners Payroll',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['Expenses', 'No', '', '', ''],
          [
            'Fire Warden Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['First Aid Allowance', 'No', '', '', ''],
          ['FIT Payment', 'No', '', '', ''],
          [
            'Fixed Annuity Payments (Private)',
            'Yes',
            'Other',
            'Pension - Private',
            'Latest Bank Statement (where paid gross) or Latest Pension Statement / Latest months payslip(s) or Pension P60',
          ],
          [
            'Flexible Benefit (We accept 100% of benefit)',
            'Yes',
            'Other',
            'Colleague Flexible Benefit',
            'Latest 3 months payslips',
          ],
          [
            'Flight Attendant Allowance',
            'Yes',
            'Other',
            'Flight Pay / Allowance',
            'Latest 3 months payslips',
          ],
          ['Footwear Allowance', 'No', '', '', ''],
          [
            'Forklift Premium',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Foster Care Income',
            'Yes',
            'Main',
            'Self-Employed',
            'Latest 2 years tax assessments',
          ],
          ['Gambling', 'No', '', '', ''],
          [
            'Get You Home Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['Guardian Allowance', 'No', '', '', ''],
          [
            'HDT Allowance (Home to Duty Travel)',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['Healthcare Allowance', 'No', '', '', ''],
          [
            'High Cost Area',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'HM Forces - Guaranteed Income Payment (GIP)',
            'Yes',
            'Other',
            'Pension - Private',
            'Latest Bank Statement (where paid gross) or Latest Pension Statement / Latest months payslip(s) or Pension P60',
          ],
          [
            "Holiday Pay (only if it forms part of basic pay and doesn't inflate income)",
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Housing Allowance (where paid by employer only)',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          ['Housing Benefit', 'No', '', '', ''],
          ['Income Support', 'No', '', '', ''],
          [
            'Industrial Injuries Disablement Benefit',
            'Yes',
            'Other',
            'Industrial Injuries Disablement Benefit',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          ['Insurance Payout', 'No', '', '', ''],
          [
            'Investment Income (Unless director or shareholder already retired - see Pension Schemes for details)',
            'No',
            '',
            '',
            '',
          ],
          [
            'Key Holder Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Leave of Absence',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Living Wage Allowance',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          ['Lodgers Income', 'No', '', '', ''],
          [
            'London Weighting',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Long Separation Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Long Service Pay',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          ['Long Term Sick', 'No', '', '', ''],
          ['Lottery', 'No', '', '', ''],
          ['Lunch Allowance', 'No', '', '', ''],
          [
            'Maintenance',
            'Yes',
            'Other',
            'Maintenance',
            'Latest 3 months Bank statements Court Order or Maintenance Assessment or letter from Child Support Agency (CSA)',
          ],
          [
            'Market Supplement',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Maternity Pay - providing the director or shareholder will return to work on the same terms.',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslip(s) received prior to commencement of maternity',
          ],
          ['Medical Negligence Annuity Payments', 'No', '', '', ''],
          [
            'Military Reservist Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Mortgage Subsidy',
            'Yes',
            'Other',
            'Mortgage Subsidy',
            'Latest 3 months payslips',
          ],
          [
            'NHS Banding',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'NHS Enhanced Pay /Enhancements (EN) or (ENH)',
            'Yes',
            'Other',
            'Shift Allowance',
            'Last 3 consecutive payslips',
          ],
          ['Non Consol Pay NP', 'No', '', '', ''],
          [
            'Nursing Bank (Main Income)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest 3 months payslips',
          ],
          [
            'Nursing Bank (Other Income)',
            'Yes',
            'Other',
            'Nursing Bank',
            'Latest 3 months payslips',
          ],
          [
            'Occupational Sick Pay',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Offshore Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'On Call Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Overtime',
            'Yes',
            'Main',
            'Annual Regular Overtime',
            'Latest 3 months payslips',
          ],
          [
            'Pension Supplement',
            'Yes',
            'Other',
            'Colleague Flexible Benefits',
            'Latest 3 months payslips',
          ],
          [
            'Pension Tax Credits',
            'Yes',
            'Other',
            'Pension Credit',
            'Latest Bank Statement or Pension Statement',
          ],
          [
            'Permanent Health Insurance (PHI) The term must not extend past the final payment date for PHI if affordability is based on this income only',
            'Yes',
            'Other',
            'Pension - Private',
            'Latest Bank Statement (where paid gross) or Latest Pension Statement / Latest months payslip(s) or Pension P60',
          ],
          [
            'Personal Independence Pay',
            'Yes',
            'Other',
            'Personal Independence Pay',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          ['Petrol Allowance', 'No', '', '', ''],
          ['Piecework', 'No', '', '', ''],
          [
            'Private Pension / Private Widowers Pension',
            'Yes',
            'Other',
            'Pension - Private',
            'Latest Bank Statement (where paid gross) or Latest Pension Statement / Latest months payslip(s) or Pension P60',
          ],
          ['Profit Related Pay', 'No', '', '', ''],
          [
            'Project Disbursement',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          ['Protection of Pay Allowance', 'No', '', '', ''],
          [
            "Public Holiday Pay (only if it forms part of basic pay and doesn't inflate income)",
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          [
            'Recruitment and Retainment Pay',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          ['Renewable Energy', 'No', '', '', ''],
          ['RHI Payment', 'No', '', '', ''],
          ['Savings', 'No', '', '', ''],
          ['Scholarship', 'No', '', '', ''],
          [
            'Scottish Island Allowance/Distant Island Allowance/Island Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          ['Seasonal Contract', 'No', '', '', ''],
          ['Secondment Allowance', 'No', '', '', ''],
          [
            'SEN Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Shift Allowance',
            'Yes',
            'Other',
            'Shift Allowance',
            'Latest 3 months payslips',
          ],
          [
            'Sleepover Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Standby Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'State Pension',
            'Yes',
            'Other',
            'Pension - State',
            'Latest Bank Statement or Pension Statement',
          ],
          [
            'State Widows Pension. (Please see above if Bereavement Allowance).',
            'Yes',
            'Other',
            'Pension - State',
            'Latest Bank Statement or Pension Statement',
          ],
          ['Statutory Maternity Pay', 'No', '', '', ''],
          [
            'Statutory Sick Pay (if returning to work see Occupational Sick Pay)',
            'No',
            '',
            '',
            '',
          ],
          [
            'Stipend (Only acceptable for Clergy)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Letter of confirmation from Religious Order',
          ],
          ['Tax Rebate', 'No', '', '', ''],
          [
            'Teaching and Learning Responsibility (TLR)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslips',
          ],
          ['Temporary Contract', 'No', '', '', ''],
          [
            'Term Time Allowance',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'The Thalidomide Trust Income',
            'Yes',
            'Other',
            'Pension - Private',
            'Latest Bank Statement (where paid gross) or Latest Pension Statement / Latest months payslip(s) or Pension P60',
          ],
          [
            'Tips',
            'Yes',
            'Main',
            'Annual Regular Overtime',
            'Latest 3 months payslips',
          ],
          ['Tool Allowance', 'No', '', '', ''],
          [
            'Town Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Travel Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Tronc',
            'Yes',
            'Main',
            'Annual Regular Overtime',
            'Latest 3 months payslips',
          ],
          ['Trust Income', 'No', '', '', ''],
          ['Uniform Allowance', 'No', '', '', ''],
          [
            'Universal Credit (The directors and shareholders on the mortgage must be the same as on the award letter and a joint income must be split between the directors and shareholders. Directors and shareholders must have an earned income for this income to be used. Any Housing Benefit shown on the award letter should be deducted from the income keyed as it will no longer apply)',
            'Yes',
            'Other',
            'Universal Credit',
            'Latest Benefit Award Letter',
          ],
          [
            'Unsocial Hours',
            'Yes',
            'Other',
            'Shift Allowance',
            'Latest 3 months payslips',
          ],
          [
            'War Disablement Pension',
            'Yes',
            'Other',
            'Pension - War Disablement',
            'Latest Bank Statement or Pension Statement',
          ],
          [
            'War Widow(er) Pension',
            'Yes',
            'Other',
            'Pension - War Widow',
            'Latest Bank Statement or Pension Statement',
          ],
          [
            'Widowed Parents Allowance',
            'Yes',
            'Other',
            'Widowed Parents Allowance',
            'Latest Bank Statement or Pension Statement',
          ],
          ['Widows Allowance', 'No', '', '', ''],
          [
            'Work Outside Normal Hours',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Working From Home Allowance',
            'Yes',
            'Other',
            'Town / Area / Car Allowance',
            'Latest months payslips',
          ],
          [
            'Working Tax Credit',
            'Yes',
            'Other',
            'Working Tax Credit',
            'Latest Bank Statement or Benefit Award Letter',
          ],
          [
            'Working Time Directive Payment (WTD)',
            'Yes',
            'Other',
            'Additional Duty Hours',
            'Latest 3 months payslips',
          ],
          [
            'Zero Hours Contract (The directors or shareholders must have been employed on a zero hours contract for a minimum of 12 months (with the same employer or with different employers but in the same type of work))',
            'Yes',
            'Main',
            'Basic Annual Income',
            "12 months' worth of payslips (this applies whether the directors and shareholders are paid weekly or monthly and could consist of P60 plus subsequent payslips to cover the full 12 month period)",
          ],
        ],
      },
      additionalInfo: {
        id: 'income-types-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Length of employment/self-employment',
            url: '/ltd-co-criteria#length-of-employment-self-employment',
          },
          { label: 'Rental income', url: '/ltd-co-criteria#rental-income' },
          {
            label: 'Self-employed directors and shareholders',
            url: '/ltd-co-criteria#self-employed-directors-and-shareholders',
          },
          { label: 'Contractors', url: '/ltd-co-criteria#contractors' },
          {
            label: 'Self-employed/contractors',
            url: '/ltd-co-criteria#self-employed-contractors',
          },
          { label: 'Second job', url: '/ltd-co-criteria#second-job' },
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
        ],
      },
    },
    lengthOfEmploymentSelfEmployment: {
      id: 'length-of-employment-self-employment',
      title: 'Length of employment/self-employment',
      paragraphs: [
        'Employed directors and shareholders will be expected to have been employed for a minimum of three months in their current job.',
        "Self-employed directors and shareholders must have been self-employed for a minimum of two years and provide at least two years' worth of trading figures.",
      ],
      additionalInfo: {
        id: 'length-of-employment-self-employment-additional-info',
        title: 'Additional information:',
        links: [
          { label: 'Contractors', url: '/ltd-co-criteria#contractors' },
          { label: 'Income types', url: '/ltd-co-criteria#income-types' },
          { label: 'Rental income', url: '/ltd-co-criteria#rental-income' },
          {
            label: 'Self-employed directors and shareholders',
            url: '/ltd-co-criteria#self-employed-directors-and-shareholders',
          },
          {
            label: 'Self-employed/contractors',
            url: '/ltd-co-criteria#self-employed-contractors',
          },
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
          {
            label: 'Affordability and sustainability',
            url: '/ltd-co-criteria#affordability-and-sustainability',
          },
          { label: 'Second job', url: '/ltd-co-criteria#second-job' },
        ],
      },
    },
    rentalIncome: {
      id: 'rental-income',
      title: 'Rental income',
      paragraph: (
        <Paragraph>
          For employed, self-employed, and retired directors and shareholders,
          all rental income and finance costs captured in the 'Land and property
          income' section of the application should match the directors and
          shareholders tax calculations from HMRC. You can find how to key this
          in our{' '}
          <Link
            href="https://www.bmsolutions.co.uk/pdf/bm-online-hints-tips.pdf"
            external
          >
            Hints & tips PDF
          </Link>{' '}
          (3.1MB).
        </Paragraph>
      ),
      toKeyADirector: {
        id: 'to-key-a-director',
        title:
          'To key a directors or shareholders whose only source of income is from land and property',
        paragraphs: [
          "Select 'Occupation type' as 'Professional' and 'Employment type' as 'Self-Employed'.",
          "You'll need to input a year end date and figures for their most recent and previous years. The most recent date can't be older than 18 months from the date of submission.",
          "The remaining figures for net profit, turnover, gross profit, current assets, and current liabilities must be input as '£0'. Complete the 'Self-employment details' - including business name and accountant details - and the 'Add employment' section. The land and property profit figures from the tax calculation will then need to be entered into the land and property income section.",
          "Any finance costs for the latest year will also need to be entered. The finance costs are referenced at the bottom of the tax calculation, as well on the individual's self-assessment tax return, and property income supplement (SA105).",
          "For 'Can income be verified?', you must answer 'Yes'.",
        ],
      },
      forAllRemortgages: {
        id: 'for-all-remortgages',
        title: 'For all remortgages and further advances',
        paragraphs: [
          'The current rental amount of the property should be keyed on the application as the current monthly rent paid by the tenant.',
        ],
      },
      forBuyToLet: {
        id: 'for-buy-to-let',
        title: 'For a buy to let purchase and let to buy remortgage',
        paragraphs: [
          'The current rental amount keyed will be the anticipated monthly rent of the property.',
          'The gross monthly rent will need to be keyed into the property schedule screen for all buy to let properties owned by any directors and shareholders named on the application.',
          'A tenancy agreement or letter from the letting agent can be used to verify the rental income keyed in the Property schedule screen.',
        ],
      },
      additionalInfo: {
        id: 'rental-income-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
          {
            label: 'Affordability and sustainability',
            url: '/ltd-co-criteria#affordability-and-sustainability',
          },
          { label: 'Income types', url: '/ltd-co-criteria#income-types' },
          {
            label: 'Self-employed directors and shareholders',
            url: '/ltd-co-criteria#self-employed-directors-and-shareholders',
          },
          {
            label: 'Self-employed/contractors',
            url: '/ltd-co-criteria#self-employed-contractors',
          },
          { label: 'Contractors', url: '/ltd-co-criteria#contractors' },
        ],
      },
    },
    secondJob: {
      id: 'second-job',
      title: 'Second job',
      paragraph1:
        "If any director or shareholder has more than one job, we'll consider the following to decide if we can take the secondary income into account.",
      listItems: [
        'Are the hours/days worked sustainable?',
        'Are the roles/skills similar?',
        "What's the distance between the jobs and the directors or shareholders home?",
        'How long has the directors or shareholders been in both jobs?',
        'Is the salary consistent with the type of employment?',
      ],
      paragraph2:
        'You should key the highest income as the directors and shareholders main income, and the income from the second job as additional employment.',
      additionalInfo: {
        id: 'second-job-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Rental income',
            url: '/ltd-co-criteria#rental-income',
          },
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
          {
            label: 'Affordability and sustainability',
            url: '/ltd-co-criteria#affordability-and-sustainability',
          },
          {
            label: 'Income types',
            url: '/ltd-co-criteria#income-types',
          },
          {
            label: 'Self-employed directors and shareholders',
            url: '/ltd-co-criteria#self-employed-directors-and-shareholders',
          },
          {
            label: 'Self-employed/contractors',
            url: '/ltd-co-criteria#self-employed-contractors',
          },
          {
            label: 'Contractors',
            url: '/ltd-co-criteria#contractors',
          },
        ],
      },
    },
    selfEmployedDirectorsOrShareholders: {
      id: 'self-employed-directors-and-shareholders',
      title: 'Self-employed directors and shareholders',
      paragraphs: [
        "Self-employed directors and shareholders must have been self-employed for a minimum of 2 years and be able to provide at least 2 years' worth of trading figures.",
      ],
      ifAnyOfTheFollowingApply: {
        id: 'self-employed-directors-and-shareholders-if-any-of-the-following-apply',
        title:
          'If any of the following applies, the director or shareholder must be keyed as self-employed:',
        listItems: [
          'They have a shareholding of 25% or more.',
          'If directors or shareholders both own a percentage share of 25% or more between them, treat them both as self-employed.',
          'A sub-contractor who earns income from more than one contract.',
          'They have a partnership interest in a business where income is not PAYE, regardless of the percentage shareholding.',
          'They own a franchise.',
          "They're employed by a private limited company (LTD) and receive a salary (PAYE) and dividends as part of their remuneration package.",
          "They're in a limited liability partnership (LLP) and receive a share of net profits.",
        ],
        paragraph:
          "All self-employed income must be entered in the 'Net profit' field, except profit from UK Land & Property, which needs to be captured in the Land and property income section.",
      },
      theNetProfitField: {
        id: 'self-employed-directors-and-shareholders-the-net-profit-field',
        title: 'The net profit field should be completed as following:',
        listItems: [
          'Limited company - key total of salary/remuneration and dividends drawn as shown on tax calculation.',
          'Partnership or LLP - key profit share percentage.',
          'Sole trader - key net profits.',
        ],
      },
      documentationNeeded: {
        id: 'self-employed-directors-and-shareholders-documentation-needed',
        title: 'Documentation needed:',
        listItems: [
          "Latest 2 years' HM Revenue & Customs (HMRC) tax calculations and tax year overviews showing years and the directors and shareholders name/initials.",
        ],
      },
      taxCalculationsAreAvailable: {
        id: 'self-employed-directors-and-shareholders-tax-calculations-are-available',
        title:
          'Tax calculations are available in the directors and shareholders online self-assessment account. If they use these, the following details must be visible:',
        listItems: [
          'HMRC logo.',
          'Unique Tax Reference (UTR).',
          'Tax year where the most recent is no older than 18 months old.',
          "Official wording that says: 'This is a copy of information held on your official online SA tax account with HMRC', and 'Submission is 100% complete.'",
        ],
      },
      onlineTaxAssessments: {
        id: 'self-employed-directors-and-shareholders-online-tax-assessments',
        title:
          "Online tax assessments from accountants' commercial software, accompanied by the corresponding tax year overviews are acceptable as long as they show:",
        listItems: [
          'UTR.',
          'Director/shareholder name.',
          'Tax year where the most recent is no older than 18 months old.',
        ],
        paragraphs: [
          'The tax due figure on the tax calculation and tax year overview will usually match. If there is a difference in the tax figure, we might need updated documents.',
          "The latest two years' full accounts must be finalised and clearly show the net profit for sole traders, the share of net profit for partnerships, or salary and dividends for directors of limited companies.",
          "Profit and loss statements alone aren't acceptable.",
          'The year end for all documents must be the most recent and not be dated more than 18 months before the date of application.',
        ],
      },
      additionalInfo: {
        id: 'self-employed-directors-and-shareholders-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Self-employed/contractors',
            url: '/ltd-co-criteria#self-employed-contractors',
          },
          {
            label: 'Contractors',
            url: '/ltd-co-criteria#contractors',
          },
          {
            label: 'Rental income',
            url: '/ltd-co-criteria#rental-income',
          },
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
          {
            label: 'Income types',
            url: '/ltd-co-criteria#income-types',
          },
          {
            label: 'Affordability and sustainability',
            url: '/ltd-co-criteria#affordability-and-sustainability',
          },
          {
            label: 'Second job',
            url: '/ltd-co-criteria#second-job',
          },
        ],
      },
    },
    selfEmployedContractors: {
      id: 'self-employed-contractors',
      title: 'Self-employed/contractors',
      table: {
        headings: [
          'Income Type',
          'Acceptable?',
          'Main or Other',
          'Treat as',
          'Evidence',
        ],
        rows: [
          [
            'Contractor - If director or shareholder earns more than £500 per day or £75,000 per annum - providing 12 months or more continuous employment with 6 months of contract remaining OR 2 years continuous service (for the last 2 years at the date of the application) in the same type of employment',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Copy of the contract and latest months personal/business bank statement(s) showing the salary credit',
          ],
          [
            'Contracts - Fixed / Short / Temporary - If Company Paying Tax (Excluding CIS contractors) - providing 12 months or more continuous employment with 6 months of the contract remaining OR 2 years continuous service (for the last 2 years at the date of the application) in the same type of employment',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslip(s)',
          ],
          [
            'Contracts Fixed / Short / Temporary - If director or shareholder paying Tax',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'IT Contractors - providing 12 months or more continuous employment with 6 months of the contract remaining OR 2 years continuous service (for the last 2 years at the date of the application) in the same type of employment',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Copy of the IT Contract and latest months personal/business bank statement(s) showing the salary credit',
          ],
          [
            'CIS Contractors - If Company Paying Tax key as employed providing 12 months or more continuous employment with 6 months of contract remaining OR 2 years continuous service (for the last 2 years at the date of the application) in the same type of employment',
            'Yes',
            'Main',
            'Basic Annual Income',
            "Latest three consecutive months payslip/invoices (this applies whether the directors and shareholders are paid weekly or monthly) and three months corresponding bank statements. P60/Payslips If no income is received or the directors and shareholders are unable to provide evidence of income then 'zero' income must be used for that month",
          ],
          [
            'CIS Contractors - If director or shareholder pays own tax or has more than one contract or is employed by Ltd company he/she has set up then treat as Self Employed',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Employed in a family business equal to or greater than 25% Invested interest and/or receives dividend or net profit as part of their reward package(Treat as Self Employed)',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Ltd Company - Director/Shareholder - Wanting to use - Basic PAYE salary ONLY less than 25% shareholding (Treat as employed)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslip(s)',
          ],
          [
            'Ltd Company - Director/Shareholder - Wanting to use - Basic PAYE salary ONLY Equal to or greater than 25% shareholding (Treat as self employed)',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Ltd Company - Director/Shareholder - Wanting to use - Basic PAYE salary + dividends ONLY Irrespective of share holding (Treat as Self Employed)',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Ltd Company - Director/Shareholder - Wanting to use - Basic PAYE salary + Net Profits ONLY irrespective of share holding (Treat as Self Employed) Note: Net Profits cannot be used without referral to Underwriters',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Self employed - Sole Trader',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Self employed / Share holding of a Non Ltd Company (ie Partnership) Taxable Income',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Partner of LLP receiving profit share as part of their salary package Irrespective of share of equity (Treat as Self employed)',
            'Yes',
            'Self Employed',
            <Link href="#self-employed-directors-and-shareholders">
              See Self Employed directors and share holders
            </Link>,
            'Last 2 years Tax Calculations and corresponding Tax Year Overviews',
          ],
          [
            'Employee of LLP - not equity shareholding partner. (Treat as employed)',
            'Yes',
            'Main',
            'Basic Annual Income',
            'Latest months payslip(s)',
          ],
          ['Seasonal Contract', 'No', '', '', ''],
          ['Piecework Contract', 'No', '', '', ''],
        ],
      },
    },
    taxOnRentalIncome: {
      id: 'tax-on-rental-income',
      title: 'Tax on rental income',
      paragraphs: [
        "Finance costs are not tax deductible, but tax relief can be claimed at the basic rate, whatever rate of tax the director or shareholder pays. This will mean an increased tax liability for some landlords. Where applications are referred for manual review, we'll consider the additional tax liability.",
        "Directors or shareholders must get independent tax advice if they're unsure how the tax rules will affect them.",
      ],
      additionalInfo: {
        id: 'tax-on-rental-income-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Rental income',
            url: '/ltd-co-criteria#rental-income',
          },
        ],
      },
    },
  },
  lending: {
    id: 'lending',
    title: 'Lending',
    assignableContracts: {
      id: 'assignable-contracts',
      title: 'Assignable contracts',
      paragraphs: [
        'Assignable contracts allow the original buyer to sell the property before legal completion, by assigning the contract to buy the property to a new buyer after contracts have been exchanged with the developer.',
        "We can't accept applications involving assignable contracts or irrevocable powers of attorney in favour of intervening sellers. Any similar transaction structure must be reported to us.",
      ],
      additionalInfo: {
        id: 'assignable-contracts-additional-info',
        title: 'Additional information:',
        links: [{ label: 'New build', url: '/ltd-co-criteria#new-build' }],
      },
    },
    bankruptcies: {
      id: 'bankruptcies',
      title: 'Bankruptcies',
      paragraph1:
        "You must answer 'Yes' to the credit history question asking if any director or shareholder has had a bankruptcy / IVA / debt management arrangement or debt relief order which was:",
      listItem1:
        'Registered within the last six years (whether now completed or not).',
      paragraph2: 'Or',
      listItem2: 'Is still outstanding (whenever this was registered).',
      paragraph3:
        "If a bankruptcy / IVA / debt management arrangement or debt relief order was registered over six years ago and is no longer outstanding, the question can be answered as 'No'.",
      additionalInfo: {
        id: 'bankruptcies-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'County Court Judgements (CCJs)',
            url: '/ltd-co-criteria#ccjs',
          },
          {
            label: 'Mortgage arrears',
            url: '/ltd-co-criteria#mortgage-arrears',
          },
        ],
      },
    },
    businessConsumerBTL: {
      id: 'business-consumer-buy-to-let',
      title: 'Business/consumer buy to let',
      paragraph1:
        'We do not offer Limited company consumer buy to let (CBTL), all Ltd company buy to lets are business buy to let (BBTL).',
    },
    concessionaryPurchase: {
      id: 'concessionary-purchase',
      title: 'Concessionary (discounted) purchase',
      paragraphs: [
        "We don't accept concessionary purchase applications where the property is being purchased below full market value.",
        'The only exception to this is the purchase of a new build property if the value has increased between agreement to purchase and building completion. Builder incentives are acceptable, subject to our existing criteria.',
        "It's acceptable to purchase a BTL property from a director or shareholders family member, as long as the deposit is sourced from the directors or shareholders own resources and the property is purchased at full market value. We can't accept gifted deposits, even if they're from a different family member to the directors or shareholders.",
        'Limited company BTL properties cannot be let to a family member.',
        'Gift of equity is accepted when the property is being purchased from director or shareholder',
      ],
      additionalInfo: {
        id: 'concessionary-purchase-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Builder cash incentives',
            url: '/ltd-co-criteria#builder-cash-incentives',
          },
        ],
      },
    },
    countyCourtJudgements: {
      id: 'ccjs',
      title: 'County Court Judgements (CCJs)',
      paragraphs: [
        "Please answer 'Yes' to the credit history question if any directors or shareholders have had a County Court Judgement (CCJ) or default registered against them, or their business if they're self-employed, within the last six years.",
        'CCJs are generally considered in credit scoring. Background details will be need for the application.',
      ],
      additionalInfo: {
        id: 'ccjs-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Mortgage arrears',
            url: '/ltd-co-criteria#mortgage-arrears',
          },
          {
            label: 'Bankruptcies',
            url: '/ltd-co-criteria#bankruptcies',
          },
        ],
      },
    },
    creditScoreDeclineAppeals: {
      id: 'credit-score-decline-appeals',
      title: 'Credit score decline appeals',
      paragraphs: [
        "All applications will be credit scored. Directors or shareholders have the right to appeal if an application is declined because it doesn't pass our credit scoring.",
        'Here are the scenarios when an appeal might be successful:',
      ],
      portingApplicationWithNoAdditionalBorrowing: {
        id: 'credit-score-decline-appeals-porting-application-with-no-additional-borrowing',
        title: 'Porting application with no additional borrowing',
        bfpoAddress: {
          id: 'credit-score-decline-appeals-porting-application-with-no-additional-borrowing-bfpo-address',
          title: 'BFPO Address',
          paragraphs: [
            'The directors or shareholders is in the British Armed Forces and living at a BFPO address.',
          ],
        },
        victimOfFraud: {
          id: 'credit-score-decline-appeals-porting-application-with-no-additional-borrowing-victim-of-fraud',
          title: 'Victim of fraud',
          paragraphs: [
            "The fraud's been reported to the police and Credit Industry Fraud Avoidance system (CIFAS) and has its own a crime reference number.",
          ],
        },
      },
      adverseCreditDataRegisteredInError: {
        id: 'credit-score-decline-appeals-adverse-credit-data-registered-in-error',
        title: 'Adverse credit data registered in error',
        anErrorByLBG: {
          id: 'credit-score-decline-appeals-adverse-credit-data-registered-in-error-an-error-by-lbg',
          title: 'An error by Lloyds Banking Group can be proven',
          paragraphs: [
            "If the adverse information has been registered on a non-Lloyds Banking Group product, an appeal can't be submitted. The directors or shareholders should arrange for their credit file to be corrected before a new application is submitted.",
            "For any scenario outside of these, it's unlikely we'll change our decision.",
          ],
          link: {
            label: 'Read our credit scoring guide',
            url: '/public/credit_scoring.pdf',
          },
        },
      },
    },
    dependants: {
      id: 'dependents',
      title: 'Dependants',
      paragraphs: [
        "A dependant is anyone who relies on your directors or shareholders financially. They won't be included on the mortgage or contribute to the monthly payments.",
        'Child and adult dependants need to be captured separately.',
      ],
      childDependants: {
        id: 'dependents-children',
        title: 'Child dependants',
        paragraphs: [
          "These are any children under 18 who the directors or shareholders are the primary carer for. You should also include any children they're expecting or in the process of adopting. Foster children or any children who have an income they can fully support themselves with don't need to be included.",
          "Child maintenance must be keyed as an outgoing commitment, but the child doesn't need to be included as a dependant.",
        ],
      },
      adultDependant: {
        id: 'dependents-adults',
        title: 'Adult dependant',
        paragraphs: [
          'An adult dependant is someone not included in the mortgage but is financially reliant on the directors or shareholders. This could be a spouse/partner, elderly relative, or any grown-up children in full-time education or unemployed.',
          "If the adult dependant doesn't live at the property all of the time, still key this in the adult dependant section.",
          "If the adult dependant doesn't ever live at the property, they must not be keyed as a dependant, but the related costs must be keyed as a total figure in 'Other commitments'.",
        ],
      },
    },
    deposits: {
      id: 'deposits',
      title: 'Deposits',
      paragraphs: [
        'The source of all director and shareholder deposits must be captured, and proof must be retained for all buy to let applications, other than remortgages.',
        "The deposit should come from the directors and shareholders own resources. A builder's deposit of up to 5% of the purchase price is allowable on new build property applications if the LTV is 75% or less. The valuer must be aware of any incentive and make a note of it in the valuation report.",
        "For certain applications, you'll need to verify the deposit. Evidence will also be needed if the application needs an underwriter review, and/or a message is displayed at DIP submission and the additional requirements section of the Submit application results screens at full application.",
      ],
      weCantAcceptDepositSources: {
        id: 'we-cant-accept-deposit-sources',
        title: "We can't accept the following as deposit sources:",
        listItems: [
          "Any deposit coming from a business current account, business loan, or directors' loan account.",
          'Intercompany loans.',
          'Vendor gifted deposits.',
          'Personal loans.',
          'Second charges.',
          'The Forces Help to Buy scheme (Long Service Advance of Pay) for military personnel.',
        ],
      },
      weCanAcceptDepositSources: {
        id: 'we-can-accept-deposit-sources',
        title:
          'We can accept the following as deposit sources, as long as they can be verified',
        directorLoans: {
          id: 'directors-loans',
          title: 'Directors Loans',
        },
        equity: {
          id: 'equity',
          title: 'Equity',
          listItems: [
            'The credit file should confirm a mortgage already exists.',
            'If no mortgage can be confirmed on the credit file, request a copy of the most recent mortgage statement. This must be dated within the last 12 months.',
            "We'll also request the sales details for the property to verify its value.",
          ],
          paragraph:
            "If the directors and shareholders are remortgaging or taking a further advance on an existing property with us, and the funds will be used as a deposit, we'll need to view the funds in an account or an offer guaranteeing the lending.",
        },
        gift: {
          id: 'gift',
          title: 'Gift',
          paragraphs1: [
            'Gifted deposits from directors or shareholders to the Limited company are acceptable',
            'Gift of equity accepted when the property is being purchased from director or shareholder',
            "Gifted deposits from family to directors or shareholders are acceptable. They must be non-repayable. The property can't be let out to, or purchased from, a family member. Evidence of the source of deposit must be kept on record in all cases.",
            "Purchases between family members are acceptable, as long as the property's sold at market value and the buyer is a UK resident. ",
          ],
          boldParagraph:
            "You'll need to provide a signed letter written by the donor within three months of the mortgage application",
          itMustInclude: {
            id: 'gift-it-must-include',
            title: 'It must include:',
            listItems: [
              'BM Solutions/Birmingham Midshires address.',
              'The address of the property being bought.',
              'Directors and shareholders names.',
              "The donor's full name and address, and relationship to the directors or shareholders.",
            ],
          },
          itMustConfirm: {
            id: 'gift-it-must-confirm',
            title: 'It must confirm:',
            listItems: [
              'The deposit amount.',
              "That the donor understands the deposit is non-repayable and they'll hold no interest in the property after mortgage completion.",
            ],
          },
          paragraphs2: [
            "You must also provide the latest UK bank statement or passbook showing evidence of the deposit funds in the donor's or directors or shareholders UK bank account. Alternatively, a letter from the donor's or directors or shareholders UK bank confirming the funds are available can be provided.",
            'Additional statements or other documents can be requested if needed.',
            <>
              View our{' '}
              <Link
                external
                href="https://www.bmsolutions.co.uk/pdf/gifted_deposit_letter_template.pdf"
              >
                gifted deposit letter template
              </Link>
            </>,
            'A family member is someone who is related to at least one of the directors or shareholders:',
          ],
          listItems: [
            'by birth/blood relative,',
            'by marriage or civil partnership, including step-children, adopted children and in-laws,',
            "as common law' partners or co-habitants.",
          ],
          table: {
            headings: ['Acceptable', 'Not Acceptable'],
            rows: [
              ['Parents, step parent, parents in laws', 'Family Friend'],
              [
                'Brothers and sisters, half brothers and sisters, step brothers and sisters, brother and sister in laws',
                'Employer',
              ],
              ['Grandparents, step grandparents', 'Developer/ Landlord'],
              [
                <>
                  <Paragraph>Auntie/Uncle (related by blood)</Paragraph>
                  <Paragraph>Niece/Nephew</Paragraph>
                </>,
                'Auntie/Uncle (related via marriage)',
              ],
              [
                'Partners living with directors and shareholders either common law or co-habitee',
                'Cousins',
              ],
              [
                'Directors and shareholders children, step children, son and daughter in laws and adopted children',
                'Foster/Guardian children',
              ],
            ],
          },
        },
        inheritance: {
          id: 'inheritance',
          title: 'Inheritance',
          paragraph:
            'We may request up-to-date UK bank statements or a passbook showing evidence of the inheritance, if needed.',
          link: {
            label: 'View Savings for more details.',
            url: '/ltd-co-criteria#uk-savings',
          },
        },
        previousPropertySale: {
          id: 'previous-property-sale',
          title: 'Previous property sale',
          paragraph: (
            <>
              Please use the guidance in the{' '}
              <Link href="/ltd-co-criteria#equity">Equity</Link> or{' '}
              <Link href="/ltd-co-criteria#uk-savings">Savings</Link> sections.
            </>
          ),
        },
        ukSavings: {
          id: 'uk-savings',
          title: 'UK Savings',
          paragraphs: [
            "If requested, you'll need to provide current account, ISAs, or savings account statements as a minimum. You must show at least three months' statements or latest annual statement with the directors or shareholders names, sort code, account number and provider name clearly visible.",
            "The latest statement must show a closing balance covering the deposit required. If there are any recent large or unusual deposits into the account, you'll need to confirm where they came from. This might mean obtaining further documents.",
            "If funds are from a business account, you must obtain confirmation from the company accountant that the withdrawal won't have a negative impact on the business.",
          ],
        },

        shares: {
          id: 'shares',
          title: 'Shares',
          paragraph: 'Please obtain:',
          listItems: [
            'a certified copy of Share Certificate showing directors or shareholders name and address,',
            'evidence of current share value calculation showing the value of shares.',
          ],
        },
        premiumBonds: {
          id: 'premium-bonds',
          title: 'Premium bonds',
          youllNeedToProvide: {
            id: 'premium-bonds-youll-need-to-provide',
            title: "You'll need to provide:",
            listItems: [
              'Premium bond certificate showing the name of the director or shareholder and the value of premium bonds held.',
              "Additional documents to verify the director or shareholder's name and address is the same as director or shareholder named on the certificate.",
            ],
          },
        },
        passbooks: {
          id: 'passbooks',
          title: 'Passbooks',
          listItems: [
            'should show director or shareholder names and account numbers,',
            'should be in the same name as at least one of the directors or shareholders named on the new mortgage,',
            "should show at least the latest three months' transactions and the closing balance must cover the deposit required.",
          ],
          paragraph:
            "If there are any recent large or unusual deposits into the account, you'll need to confirm where they came from. This might mean obtaining further documents.",
        },
        additionalInfo: {
          id: 'deposits-additional-info',
          title: 'Additional information:',
          links: [
            {
              label: 'Builder cash incentives',
              url: '/ltd-co-criteria#builder-cash-incentives',
            },
          ],
        },
      },
    },
    firstTimeBuyerFirstTimeLandlord: {
      id: 'first-time-buyer-first-time-landlord',
      title: 'First-time buyer/first-time landlord',
      paragraph:
        'The directors must own a property in the UK in their own name. This will need to be validated before an offer can be made.',
      moreThanOneDirector: {
        id: 'first-time-buyer-first-time-landlord-more-than-one-director',
        title: 'More than one director or shareholder',
        paragraphs: [
          'At least one director or shareholder must own a property in the UK in their own name. This will need to be validated before an offer can be made. All other named directors or shareholders can be first time buyers.',
          "In most cases, we'll validate property ownership with a credit search on the directors or shareholders. If we can't do this or the property is unencumbered, we might ask for more evidence.",
        ],
      },
      acceptableDocuments: {
        id: 'first-time-buyer-first-time-landlord-acceptable-documents',
        title: 'Acceptable documents:',
        englandAndWales: {
          id: 'first-time-buyer-first-time-landlord-acceptable-documents-england-and-wales',
          title: 'England and Wales',
          listItems: [
            'Land Registry certificate dated within the last three months, which must show at least one of the directors or shareholders names.',
            'A copy of a recent mortgage statement dated within the last three months, which must show at least one of the directors or shareholders names.',
            'A copy of a mortgage statement over three months old plus last three months bank statements confirming payment. Both must show at least one of the directors or shareholders names.',
          ],
        },
        scotlandAndNorthernIreland: {
          id: 'first-time-buyer-first-time-landlord-acceptable-documents-scotland-and-northern-ireland',
          title: 'Scotland and Northern Ireland',
          listItems: [
            'Land Registry certificate dated within the last three months. This must show at least one of the directors or shareholders names. If no date is on the certificate, proof this was requested within the last three months is acceptable from the Land Registry or a solicitor.',
            'A copy of a recent mortgage statement dated within the last three months, which must show at least one of the directors or shareholders names.',
            'A copy of a mortgage statement over three months old plus last three months bank statements confirming payment. Both must show at least one of the directors or shareholders names.',
          ],
        },
      },
    },
    lettingCriteria: {
      id: 'letting-criteria',
      title: 'Letting criteria',
      thePropertyCanBeLetOn: {
        title: 'The property can be let on:',
        listItems: [
          'a single assured shorthold tenancy in England,',
          'an occupation contract in Wales,',
          'a private residential tenancy in Scotland.',
        ],
        paragraphs: [
          'The minimum tenancy term is six months. The maximum tenancy term is normally 36 months. A maximum tenancy of five years is acceptable under a Dept for Levelling Up, Housing and Communities (DLUHC) model agreement of September 2014 (or as amended) or the Scottish Equivalent.',
          "A BTL remortgage application can't be started without a tenant in place. A tenancy agreement must also be in place. If a tenancy is about to start, there must be a tenancy agreement dated within 14 days of the application confirming the expected rental income.",
          "The property can be let to a company on a corporate let. The maximum term for this is 12 months. Our consent isn't needed to renew the tenancy. But the occupiers need to be named on the tenancy and must be employees of the tenant company.",
          "The maximum number of unrelated tenants is five. They must all be named on the agreement. It's the landlords' responsibility to make sure all legislation is complied with, including (but not restricted to) legislation relating to Tenancy Deposit schemes and HMOs (Houses in Multiple Occupation).",
          'For larger properties, where there is a potential of more than five lettable rooms, the valuer must be satisfied the property and local market reflects single family letting arrangements.',
          "Properties subject to Local Authority Homes in Multiple Occupation (HMO) licensing might be acceptable, subject to the valuer's assessment of suitability based on our requirements.",
        ],
      },
      weCantAcceptTenants: {
        title: "We can't accept tenants in the following categories:",
        listItems1: [
          'Asylum seekers.',
          'Persons who have or might acquire diplomatic immunity.',
          'Social landlords, such as housing associations.',
          'Holiday lets.',
          'Rent act protected tenants.',
          'Agents of any of the above.',
          'Family members of any directors or shareholders.',
        ],
        paragraphs: [
          'The tenancy agreement must provide that assignment or sub-lettings to tenants in these categories are not allowed.',
        ],
        listItems2: [
          "Multiple tenancies, where each tenant signs a separate agreement, aren't acceptable.",
          'If the property is in Scotland, the landlord must register with the relevant local authority.',
          "Leaseback and sale properties aren't acceptable.",
          "Limited company purchases or Trust Deed applications aren't acceptable.",
          "The property can't have any occupancy restrictions.",
          "Applications won't be accepted where a second charge is being completed at the same time as the BTL application.",
        ],
      },
      additionalInfo: {
        id: 'letting-criteria-additional-info',
        title: 'Additional information:',
        link: {
          label: 'Houses in multiple occupation',
          url: '/ltd-co-criteria#houses-in-multiple-occupations',
        },
      },
    },
    lendingLimits: {
      id: 'lending-limits',
      title: 'Lending limits',
      paragraphs1: [
        <>
          A <Strong>£5,000,000 lending limit</Strong> applies to all Buy To Let,
          Let To Buy, consent to let, second home loan, and House 2 House
          borrowing across the Retail division of Lloyds Banking Group.
        </>,
        'The maximum loan available on individual properties is',
      ],
      listItems: [
        '65% LTV between £1,000,000 and £2,000,000.',
        '75% LTV up to £1,000,000.',
      ],
      paragraphs2: [
        'This includes New Build properties and there is no variation in LTV limits for flats.',
        'A maximum of 10 properties applies to Limited company buy to let, let to buy, buy to let, consent to let, and House 2 House lending across the Retail division of Lloyds Banking Group. This consists of Bank of Scotland, Birmingham Midshires, TMB, Halifax, Intelligent Finance, Lloyds Bank, C&G, and Scottish Widows.',
        'Where more than 10 properties are held, no additional borrowing is allowed.',
        <>
          The minimum loan amount is <Strong>£25,001.</Strong> For further
          advances, the minimum loan amount is <Strong>£5,000.</Strong>
        </>,
        'Certain buy to let deals have a lower max LTV. Where a deal with a lower LTV is combined with a deal with a higher LTV, the lower LTV will apply to the whole loan.',
      ],
      additionalInfo: {
        id: 'limits-additional-info',
        title: 'Additional information:',
        links: [
          { label: 'Min/max term', url: '/ltd-co-criteria#min-max-term' },
          { label: 'Min/max age', url: '/ltd-co-criteria#min-max-age' },
          {
            label: 'Portfolio landlords',
            url: '/ltd-co-criteria#portfolio-landlords',
          },
        ],
      },
    },
    minMaxAge: {
      id: 'min-max-age',
      title: 'Min/max age',
      paragraphs: [
        'The minimum director or shareholder age is 21.',
        'The eldest directors or shareholders must submit the application before their 75th birthday. They must be no older than age 80 at the end of the mortgage term.',
      ],
      additionalInfo: {
        id: 'min-max-age-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Lending limits',
            url: '/ltd-co-criteria#lending-limits',
          },
          {
            label: 'Min/max term',
            url: '/ltd-co-criteria#min-max-term',
          },
        ],
      },
    },
    minMaxTerm: {
      id: 'min-max-term',
      title: 'Min/max term',
      paragraphs: [
        'The maximum term is 40 years. The term must not exceed the eldest directors or shareholders 80th birthday.',
        'The minimum term must not be less than the selected product term.',
      ],
      additionalInfo: {
        id: 'min-max-term-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Min/max age',
            url: '/ltd-co-criteria#min-max-age',
          },
          {
            label: 'Lending limits',
            url: '/ltd-co-criteria#lending-limits',
          },
        ],
      },
    },
    mortgageArrears: {
      id: 'mortgage-arrears',
      title: 'Mortgage arrears',
      paragraph:
        "Please answer 'Yes' to the credit history question if any directors or shareholders have been in arrears in the last six years with any mortgage borrowing or ever had a property repossessed. Applications where previous mortgages have been in arrears will be considered on an individual basis.",
      additionalInfo: {
        id: 'mortgage-arrears-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'County Court Judgements (CCJs)',
            url: '/ltd-co-criteria#ccjs',
          },
          {
            label: 'Bankruptcies',
            url: '/ltd-co-criteria#bankruptcies',
          },
        ],
      },
    },
    mortgageFreeUnencumberedProperties: {
      id: 'mortgage-free-unencumbered-properties',
      title: 'Mortgage free/unencumbered properties',
      paragraphs: [
        'Loans on mortgage-free properties are treated like remortgages.',
        'The property must have been registered in at least one of the directors or shareholders names for a minimum of six months.',
      ],
      additionalInfo: {
        id: 'mortgage-free-unencumbered-properties-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Remortgage and capital raising',
            url: '/ltd-co-criteria#remortgage-and-capital-raising',
          },
          { label: 'Portability', url: '/ltd-co-criteria#portability' },
          {
            label: 'Business/consumer BTL',
            url: '/ltd-co-criteria#business-consumer-buy-to-let',
          },
          {
            label: 'Letting criteria',
            url: '/ltd-co-criteria#letting-criteria',
          },
        ],
      },
    },
    notionalStressRates: {
      id: 'notional-stress-rates',
      title: 'Notional stress rates',
      paragraph:
        'Lending amounts are either assessed using actual or anticipated rental income from the property only using stress rates and rental cover ratios.',
      additionalInfo: {
        id: 'notional-stress-rates-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Credit score decline appeals',
            url: '/ltd-co-criteria#credit-score-decline-appeals',
          },
          { label: 'Rental income', url: '/ltd-co-criteria#rental-income' },
          {
            label: 'Tax on rental income',
            url: '/ltd-co-criteria#tax-on-rental-income',
          },
          { label: 'Calculators', url: routes.Calculators },
        ],
      },
    },
    portfolioLandlords: {
      id: 'portfolio-landlords',
      title: 'Portfolio landlords',
      paragraph:
        'A portfolio landlord application is where the directors and shareholders have four or more mortgaged BTL properties, including the new transaction. This is based on the total number held by everyone named on the application.',
      heresAnExample: {
        id: 'portfolio-landlords-heres-an-example',
        title: "Here's an example of how it works",
        paragraphs: [
          'Two directors and shareholders are buying a property together. Director 1 already has two mortgaged BTL properties, and director 2 has one mortgaged BTL property.',
          "In total, they'll have four properties between them, including the one they're buying together, so the application is classified as a portfolio landlord.",
          'Mortgaged BTL properties include those held in a personal name, limited company name, or any other legal entity.',
        ],
      },
      ourProcess: {
        id: 'portfolio-landlords-our-process',
        title: 'Our process',
        listItems: [
          'Submit all applications online at BM Solutions.',
          'A question will be asked to find out the number of mortgaged BTLs on the application.',
          '3 or less, including the new application, will continue as current process.',
          '4-10, including the new application, will need proof of income and supporting documents to be uploaded.',
          "If mortgaged properties within the portfolio are held in a limited company, we'll need the latest mortgage statement or mortgage offer, if completed in the last 12 months.",
          'Full information about the portfolio must be keyed.',
        ],
      },
      informationNeededIncludes: {
        id: 'portfolio-landlords-information-needed-includes',
        title: 'Information needed includes:',
        listItems: [
          'Addresses.',
          'Year built and year of purchase.',
          'Original purchase price.',
          'Gross monthly rent.',
          'Estimated property valuation.',
          'Whether property is leasehold, managed, and mortgaged.',
        ],
        boldParagraph:
          'Your application will not progress until all of the above has been fully completed - including completion of the portfolio details screen in the full application.',
      },
      portfolioLandlordsCriteria: {
        id: 'portfolio-landlords-criteria',
        title: 'Portfolio landlords criteria',
        listItems: [
          'No more than 10 BTL mortgaged properties, including the new application.',
          'Minimum income of £30,000 each application from employment, self-employment, property portfolio profit, or pension/investment income.',
          'Maximum aggregate portfolio* of 75% LTV.',
          'Minimum aggregate portfolio rental cover ratio of 145% stressed at 5.5%.',
          'Property must meet standard criteria.',
          <>
            Please check the maximum amount the directors and shareholders can
            borrow{' '}
            <Link
              external
              href="https://www2.bmsolutionsonline.co.uk/tools/rental-income-calculator"
            >
              here.
            </Link>
          </>,
        ],
        paragraph:
          '* Based on the entire portfolio, including acquiring property and any unencumbered let property.',
      },
      unencumberedLetProperties: {
        id: 'portfolio-landlords-unencumbered-let-properties',
        title: 'Unencumbered let properties',
        paragraph:
          "These aren't included in the total mortgaged BTL properties. But the rent and property value must be declared in the property schedule and portfolio details sections of the application.",
      },
      financeCosts: {
        id: 'portfolio-landlords-finance-costs',
        title: 'Finance costs',
        paragraphs: [
          'Since 06 April 2020, tax relief on finance costs for landlords of residential properties is restricted to basic rate of income tax, regardless of which tax band the landlord falls into. Accordingly, profit from land and property as it appears on a tax calculation no longer has finance costs deducted.',
          "You'll need to input the latest year's finance costs on the application.",
        ],
      },
      portfolioLandlordHelpAndSupport: {
        id: 'portfolio-landlords-help-and-support',
        title: 'Portfolio landlord help and support',
        paragraphs: [
          "If you need any help with an application, get in touch with our specialist team. You'll get your own dedicated case handler, who will contact you once you submit your application.",
          <>
            <Strong>Call:</Strong>{' '}
            <Link href="tel:03456020914">0345 6020914</Link>
          </>,
        ],
      },
      additionalInfo: {
        id: 'portfolio-landlords-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Lending limits',
            url: '/ltd-co-criteria#lending-limits',
          },
        ],
      },
    },
    remortgageAndCapitalRaising: {
      id: 'remortgage-and-capital-raising',
      title: 'Remortgage and capital raising',
      paragraph:
        'For remortgage applications, including unencumbered properties, the Limited company must have owned the property for a minimum of six months.',
      unacceptableReasonsForCapitalRaising: {
        title: "The following reasons for capital raising aren't acceptable:",
        listItems: [
          'Gambling.',
          'Capital injection into a business.',
          'Business start-up.',
          'Timeshare purchase.',
        ],
        paragraphs: [
          "We'll allow capital raising for a deposit for a BTL purchase in personal or limited company name.",
          'Underwriters might ask for evidence of how funds will be used if they have any concerns.',
        ],
      },
      additionalInfo: {
        id: 'remortgage-and-capital-raising-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Mortgage free properties (unencumbered properties)',
            url: '/ltd-co-criteria#mortgage-free-unencumbered-properties',
          },
          { label: 'Portability', url: '/ltd-co-criteria#portability' },
          {
            label: 'Business/consumer BTL',
            url: '/ltd-co-criteria#business-consumer-buy-to-let',
          },
          {
            label: 'Letting criteria',
            url: '/ltd-co-criteria#letting-criteria',
          },
        ],
      },
    },
    repaymentMethods: {
      id: 'repayment-methods',
      title: 'Repayment methods',
      paragraph:
        'Repayment, interest only, or a combination of both are acceptable. You must tell us interest only elements will be repaid.',
    },
  },
  property: {
    id: 'property',
    title: 'Property',
    location: {
      id: 'location',
      title: 'Location',
      paragraph:
        'Properties must be in England, Scotland or Wales. Applications for properties in Northern Ireland are unacceptable.',
    },
    builderCashIncentives: {
      id: 'builder-cash-incentives',
      title: 'Builder cash incentives',
      paragraphs: [
        'Examples of builder cash incentives include deposit contributions, cash-backs, contribution to legal fees/stamp duty, and mortgage subsidies.',
        "Builder cash incentives are usually acceptable if their value isn't more than 5% of the lower of the purchase price or valuation. Cash incentives more than this might mean a lower maximum loan amount is available.",
        'All builder cash incentives must be declared at the point of sale. All lending decisions are based on either the valuation or purchase price, whichever is lower.',
      ],
      additionalInfo: {
        id: 'builder-cash-incentives-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'New build',
            url: '/ltd-co-criteria#new-build',
          },
        ],
      },
    },
    buyToLetPropertyAddress: {
      id: 'buy-to-let-property-address',
      title: 'Buy to let property address',
      paragraph:
        'The buy to let property address must not be the same as the directors or shareholders correspondence address.',
    },
    cladding: {
      id: 'cladding',
      title: 'Cladding',
      propertiesInEnglandAndWales: {
        title: 'Properties in England and Wales',
        paragraphs: [
          "Properties in buildings five storeys or higher no longer need an External Wall System (EWS1) form in order to progress an application. We'll also consider applications on properties where cladding remediation work is needed but not yet completed.",
          'EWS1 forms might still be needed for buildings four storeys or lower. The surveyor will advise when an External Wall System (EWS1) form is required to confirm the status of the external wall system and any applicable attachments e.g. balconies on multi occupied, multi storey buildings of any height.',
        ],
      },
      propertiesInScotland: {
        title: 'Properties in Scotland',
        paragraphs: [
          'Will still need an EWS1 form regardless of height. Directors or shareholders will have to source the EWS1 form themselves.',
          'The building owner and/or agent is responsible for confirming an external wall system or attachments, such as a balcony, on buildings containing flats has been assessed by a suitable expert.',
          <>
            The EWS1 form must be prepared by a qualified independent
            professional. They must be a member of one of the professional
            bodies{' '}
            <Link external href="https://www.rics.org/news-insights">
              listed by RICS.
            </Link>
          </>,
          "We can't confirm if a property is acceptable until a valuation has been instructed.",
          "For purchase cases, a special condition will be added to the mortgage offer to instruct the conveyancer to advise the directors or shareholders an offer has been made based on the information in an EWS1. It'll also make sure the directors or shareholders know neither us nor the surveyor are liable for information in the EWS1 form.",
        ],
      },
      newBuildHighRiseBlocks: {
        title: 'New build high rise blocks',
        paragraphs: [
          'In England and Wales, The Building (Amendment) Regulations 2018 or local equivalent can be used for new builds over six storeys or 18 metres high. A conveyancer will need to confirm this has been used by the building owner or agent.',
          'This applies to new builds of four storeys or 11 metres high in Scotland.',
          "If this can't be confirmed, please follow the EWS1 process.",
        ],
      },
    },
    condition: {
      id: 'condition',
      title: 'Condition',
      paragraphs: [
        'The property must be habitable, readily lettable or saleable, and structurally sound.',
        'Buildings insurance also needs to be able to be arranged on it.',
        <>
          For details of our what we need where a property has been converted
          into flats or is part of a purpose-built block, see{' '}
          <Link href="/ltd-co-criteria#tenure">Tenure of flats.</Link>
        </>,
      ],
    },
    construction: {
      id: 'construction',
      title: 'Construction',
      paragraphs: [
        "The property must be in the UK under UK legal jurisdiction. Property acceptability is based on a satisfactory valuation report from the bank's appointed valuer.",
        'Non-standard construction will be assessed on individual merit.',
        "Certain types of pre-cast reinforced concrete (PRC) construction are classed as defective. We might not accept it unless it's been repaired.",
        "We'll be guided by the suitability of all mortgage properties by the valuer.",
      ],
      listItems: [
        "Adjoining property purchases are acceptable, subject to valuer's comments.",
        "Houseboats and mobile homes/park homes aren't acceptable.",
        'Properties with annexes are acceptable as long as the annexe forms part of the main title. There should also be no separate tenancy agreement in place for the annexe.',
        "Studio flats are acceptable subject to valuer's comments. There is no minimum size requirement.",
        "Right to Buy/Shared ownership applications aren't acceptable.",
        'If a flat is part of a conversion, where the total number of flats in the scheme is above eight, the valuer must confirm the whole conversion project has been or will be completed to a high standard.',
        "Flats above/next to a business owned by the same person aren't acceptable.",
        "Flats above/next to a business not owned by the same person are subject to valuer's comments.",
      ],
    },
    distressedSaleAndLeaseback: {
      id: 'distressed-sale-and-leaseback',
      title: 'Distressed sale and leaseback',
      paragraph:
        "Applications involving a distressed sale, or a sale and leaseback aren't acceptable.",
    },
    energyPerformanceCertificate: {
      id: 'energy-performance-certificate',
      title: 'Energy performance certificate',
      paragraph:
        'An energy performance certificate (EPC) gives domestic properties an energy-efficiency rating from A to G. The EPC is valid for 10 years. If the property is sold in that time, the EPC can be used by the new owner.',
      listItems: [
        'Properties in England, Northern Ireland, Scotland, and Wales must have a valid EPC rating of E or above.',
        'Purchase, remortgage, and Let to Buy applications on properties with F or G EPC ratings are only allowed where a registered exemption is in place.',
        'We do not accept further advance applications where the EPC rating is F, G or an exemption is in place, except when the purpose of loan is for BTL repairs.',
        'Product transfers are exempt.',
        'For new build properties, use the Predicted Energy Assessment (PEA) or Standard Assessment Procedure (SAP) rating if no EPC is available.',
        "If a property is exempt, evidence isn't needed unless we request it.",
        'Evidence of EPC, PEA, SAP, or exemption evidence should be kept on file.',
      ],
    },
    exposureLimits: {
      id: 'exposure-limits',
      title: 'Exposure limits',
      paragraphs: [
        "When a site is assessed as suitable for lending, we'll no longer apply an initial cap on our lending exposure. We'll continue to monitor applications (exposure) as the development progresses and might restrict further lending if concerns arise.",
        'There might be a small number of sites where we decide not to lend on because of specific market or site conditions.',
      ],
    },
    freeValuations: {
      id: 'free-valuations',
      title: 'Free valuations',
      paragraphs: [
        "If a free valuation is selected as part of a product incentive, the appeal process on the property valuation isn't available.",
        'If you want to appeal the rental valuation on a BTL remortgage application, please contact us.',
      ],
    },
    housesInMultipleOccupations: {
      id: 'houses-in-multiple-occupations',
      title: 'Houses in multiple occupations',
      paragraph:
        "Properties subject to Local Authority Homes in Multiple Occupation (HMO) licensing might be acceptable, subject to the valuer's assessment of suitability based on our requirements:",
      listItems: [
        "The maximum number of unrelated tenants is five. They must all be named on the agreement. It's the landlords' responsibility to make sure all legislation is complied with, including (but not restricted to) legislation relating to Tenancy Deposit schemes and HMOs.",
        'The property must be suitable for standard residential occupation.',
        "Institutional type properties, for example hostel accommodation/bedsits/shared accommodation comparable to halls of residence, aren't acceptable.",
        'For larger properties, where there is a potential of more than five lettable rooms, the valuer must be satisfied the property and local market reflects single family letting arrangements.',
        'The property can be let on a single assured shorthold tenancy in England and Wales, or a private residential tenancy in Scotland.',
      ],
      additionalInfo: {
        id: 'houses-in-multiple-occupations-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Letting criteria',
            url: '/ltd-co-criteria#letting-criteria',
          },
        ],
      },
    },
    japaneseKnotweed: {
      id: 'japanese-knotweed',
      title: 'Japanese knotweed',
      paragraphs: [
        "We'll lend if the valuer confirms the property is suitable mortgage security and provides a present condition valuation figure.",
        "To assess the property, the valuer will need a report outlining the issues and a quote from a treatment company. The company must either be a member of the Property Care Association or offer a warranty-backed treatment plan. If treatment is needed, it's often over 3-4 years.",
      ],
    },
    newBuild: {
      id: 'new-build',
      title: 'New build',
      paragraph1:
        'An initial occupancy/new build property is classed as any property being occupied and/or sold for the first time on the open market in its current state. It also includes newly converted and refurbished properties. These will fall into one of the following categories:',
      listItems1: [
        'Newly built property.',
        'Refurbished property. These are usually as initial occupancy where the vendor is a builder/developer and the property has been vacated, so it can be refurbished.',
        'Newly converted property. An example of this is a barn or warehouse converted into residential flats or houses.',
        "A property, either new or converted, that's been tenanted and is now offered for sale for the first time on the open market by the builder/developer.",
      ],
      paragraph2:
        'The property must have a Building Standards Indemnity Scheme from a warranty provider accepted by Lloyds Banking Group:',
      listItems2: [
        'ABC+.',
        'Advantage HCI.',
        'ARK Insurance.',
        'Building LifePlans Ltd (BLP).',
        'Buildzone.',
        'Checkmate.',
        'Global Home Warranties.',
        'HomeProof (previously Aedis).',
        'International Construction Warranties (ICW).',
        'NHBC.',
        'One Guarantee.',
        'Premier Guarantee - includes the LABC New Homes Warranty and LABC Hallmark.',
        'Protek.',
        'Q Assure.',
      ],
      forPropertyToQualify: {
        title:
          'For the property to qualify as acceptable for monitoring by a consultant, it must be either:',
        listItems: [
          'A single detached property - either self-built or otherwise - where construction is monitored by a consultant employed directly by the directors or shareholders.',
          'A property built by a self-build group, where construction is monitored by a consultant employed by that self-build group.',
          'A property forming part of a small, solely residential development of not more than 15 units in total, where construction is monitored by a consultant employed by the builder or developer. If flats/maisonettes are involved, each individual flat/maisonette counts as one property. Developments containing more than 15 units need an acceptable form of building standards indemnity.',
        ],
      },
    },
    revisedPropertyDetails: {
      id: 'revised-property-details',
      title: 'Revised property details',
      paragraphs: [
        "If the purchase property address changes once a mortgage application has been made, and the Limited company's original mortgage product has been withdrawn, a new deal from the current range must be selected.",
        "If the property being purchased isn't changing but an amendment is needed to the address keyed, such as the property number or street name, this must be changed by us before completion. Please contact us to make this change.",
      ],
    },
    scottishValuations: {
      id: 'scottish-valuations',
      title: 'Scottish valuations',
      paragraphs: [
        'We can no longer accept re-type/transcription reports from a Generic Mortgage Valuation Report (GMVR) with a valid Home Report.',
        <>
          For further clarification,{' '}
          <Link href={routes.BDMSearch}>please contact your BDM</Link>
        </>,
      ],
    },
    subSalesAndBackToBackTransactions: {
      id: 'sub-sales-and-back-to-back-transactions',
      title: 'Sub-sales and back-to-back transactions',
      paragraphs: [
        "Sub-sales and back-to-back transactions aren't acceptable.",
        'The current vendor must have owned the property for at least six months before we can accept an application to purchase the property.',
        'We use the date of registration at the Land Registry to work out the length of ownership.',
        'A back-to-back transaction is a type of sub-sale where the intervening seller buys from the original seller and sells on to the borrower on the same day or within a few days. We also class cases where the seller acquires the freehold - or superior leasehold - title to the property, and immediately sells on to the borrower by grant of lease or sub-lease as sub-sales.',
      ],
      wellAcceptBeingSoldWhereSalesAre: {
        title:
          "We'll accept the property being sold on within six months of acquisition where sales are by:",
        listItems: [
          'a personal representative of the registered proprietor,',
          'an institutional mortgagee exercising its power of sale,',
          'a receiver, trustee-in-bankruptcy or liquidator,',
          'a developer or builder selling a property acquired under a part-exchange scheme,',
          'a registered housing provider or housing association exercising a power of sale.',
        ],
        paragraphs: [
          'The conveyancer will be responsible for making sure the application meets the acceptable criteria.',
          "Applications involving assignable contracts or irrevocable powers of attorney in favour of intervening sellers aren't acceptable. You should tell us about any other transaction structure with a similar effect.",
        ],
      },
    },
    tenure: {
      id: 'tenure',
      title: 'Tenure',
      paragraph: [
        'Freehold and leasehold houses are acceptable. Leasehold properties must have a minimum of 70 years left on the lease at application.',
        "If a property's been converted into no more than four units or is within a purpose-built block, any leases should already be in place and not created at the same time as the mortgage application.",
        "We'll consider lending in these situations:",
      ],
      tenureLeaseholdDoesNotOwnFreehold: {
        title:
          'Tenure is leasehold and the Limited company does not own the freehold or share of freehold:',
        listItems: [
          'The conveyancer will make sure the lease meets our standard requirements.',
        ],
      },
      tenureLeaseholdWillOwnWholeFreehold: {
        title:
          'Tenure is leasehold and the Limited company owns/will own the freehold of the whole building on completion:',
        listItems: [
          'Our charge must be registered on the freehold of the whole building and the leasehold interest of the unit. The conveyancer will make sure the lease meets our standard requirements.',
          'The mortgage must be in the same name as the freehold and leasehold titles.',
          "It's only acceptable for the Limited company to own one unit in the whole building, including any new application.",
        ],
      },
      tenureLeaseholdWillOwnShareFreehold: {
        title:
          'Tenure is leasehold and the Limited company owns/will own on completion a share of the freehold of the whole building:',
        listItems: [
          'Our charge must be registered on the leasehold interest only. The conveyancer will make sure the lease meets our standard requirements.',
          "It's only acceptable for the Limited company to own one unit in the whole building, including any new application.",
        ],
      },
      tenureFreehold: {
        title: 'Tenure is freehold:',
        listItems: [
          'The security will be registered on the freehold of the whole building and processed as a freehold house.',
          'The Limited company must own the freehold in their own name.',
          "The mortgage and freehold title must be in the same name. It's not acceptable to hold the freehold in a Ltd Company with the customer as sole director, majority shareholder, or with any family member.",
          "All units other than the freehold unit on which we're lending must have a minimum lease of 70 years.",
          "It's only acceptable for the Limited company to own one unit in the whole building, including any new applications.",
        ],
      },
      moreThanFourUnits: {
        title: 'For developments of more than four units:',
        listItems: [
          'Tenure must be leasehold.',
          'A management company must be in place.',
          'The Limited company can hold a share in the management company.',
          'The conveyancer will make sure arrangements in connection with the management company are acceptable.',
          'Our mortgage must be registered on the leasehold title.',
        ],
      },
    },
    unacceptableConstructionTypes: {
      id: 'unacceptable-construction-types',
      title: 'Unacceptable construction types',
      listItems: [
        'Timber or metal framed buildings where the cavity has been retrospectively filled with an insulation material.',
        'Concrete walls built in Cornwall or Devon before 1950. Or 1960 for postcodes PL12, 13, 14, 15, 17, 18, 22, and 23, where a valuer has recommended a Mundic report, and a concrete test has classified it as class B or C.',
        'Unrepaired, designated defective properties under the Housing Act 1985, Housing (Scotland) Act 1987, Housing (Northern Ireland) Order 1986.',
        'Large panel system type lats or maisonette blocks without an acceptable structural appraisal.',
        'Load bearing panels made of asbestos or gypsum plaster.',
        'Structurally unsound or uninsurable properties.',
      ],
    },
    useOfProperty: {
      id: 'use-of-property',
      title: 'Use of property',
      notAcceptableAsSecurity: {
        title: "The following property types aren't acceptable as security:",
        listItems: [
          'A dwelling on a single title must not be split into separate units of accommodation, such as a house converted into two flats, so each flat would need its own title.',
          'property purchased from and let to the same family member.',
          'Bedsits.',
          'Commercial property.',
          'Ex-local authority flats, unless the majority of the building is owner-occupied.',
          'Farms or property with land subject to commercial agricultural use.',
          'Live/work units.',
          'Mobile homes/houseboats.',
          'Property assessed as unsuitable security by the valuer.',
          'Property with restricted occupancy.',
          'Property designated defective under the Housing Defects Act 1984.',
          "Properties where buildings insurance can't be arranged.",
          'Right to Buy/Shared ownership applications.',
          'Timeshares.',
          'Properties with an Energy Performance Certificate (EPC) rating of F or G with no registered exemption in place.',
        ],
      },
    },
    valuation: {
      id: 'valuation',
      title: 'Valuation',
      paragraphs: [
        'The minimum acceptable property value or purchase price is £50,000 - whichever is the lower.',
        "A mortgage valuation is valid for 12 months. If a mortgage doesn't complete within 12 months of the valuation being carried out, a new valuation will be needed. We'll advise you if a new valuation fee is also needed.",
      ],
      additionalInfo: {
        id: 'valuation-additional-info',
        title: 'Additional information:',
        links: [
          {
            label: 'Scottish valuations',
            url: '/ltd-co-criteria#scottish-valuations',
          },
        ],
      },
    },
  },
  existingCustomer: {
    id: 'existing-customer',
    title: 'Existing customer',
    arrears: {
      id: 'arrears',
      title: 'Arrears',
      paragraph:
        'Standalone product transfers can be processed as soon as the account is up to date without the need to wait three months.',
    },
    ercs: {
      id: 'ercs',
      title: 'Early repayment charges',
      paragraphs: [
        'Some of our products have early repayment charges (ERCs). These will apply if your directors and shareholders repays some or all of their mortgage in the ERC period.',
        'For mortgage products with ERCs, up to 10% of the amount outstanding can be repaid without having to pay an ERC. We can change or withdraw this at any time.',
        'On a porting application, any ERC, which might apply will be waived or refunded if the ERC period ends in the next three months.',
        'On a product transfer application, any ERC which might apply will be waived or refunded if the ERC period ends in the next four months.',
        "You can't start a product transfer application more than four months before the ERC period ends.",
        'If the account has multiple sub-accounts with different ERC periods, you can only complete a product transfer application on the sub-account with the ERC expiring the soonest.',
        "Once the application has been submitted and the new product has started, you'll be able to apply for a product transfer on the other sub-accounts. Instead, you can wait until the ERC on all sub-accounts have three months left until expiry and apply for a product transfer application on all eligible sub-accounts.",
        'For product transfers and further advances, ERCs will be charged on any overpayments made over the 10%, even if the customer is within four months of the ERC expiry.',
      ],
    },
    furtherAdvances: {
      id: 'further-advances',
      title: 'Further advances',
      paragraph:
        'We currently do not allow further lending for Ltd company mortgages.',
    },
    overpayments: {
      id: 'overpayments',
      title: 'Overpayments',
      link: {
        label: 'Making Overpayments | Knowledge Base | Birmingham Midshires',
        url: 'https://www.bmmortgages.co.uk/knowledge-base/making-overpayments',
      },
      paragraph: (
        <>
          One-off or regular overpayments can be made at any time. If early
          repayment charges (ERCs) apply to the product, please refer to our{' '}
          <Link
            href="/ltd-co-criteria#ercs"
            data-testid="overpayments-erc-link"
          >
            ERC section
          </Link>{' '}
          for more details.
        </>
      ),
    },
    paymentHolidays: {
      id: 'payment-holidays',
      title: 'Payment holidays',
      paragraph: "Payment holidays aren't allowed.",
    },
    portability: {
      id: 'portability',
      title: 'Portability',
      paragraph: [
        "Any early repayment charges (ERCs) won't have to be paid on existing BTL mortgage products ported to a new application.",
        "If a product is portable, it will be outlined in the original mortgage offer and supporting documentation. You can't port a product to a remortgage application if the property is currently mortgaged. You can port to a remortgage of an unencumbered (mortgage free) property the director or shareholder already owns or a new purchase.",
        "Porting to an unencumbered remortgage can only be done using the 'Remortgage own conveyancer' option. You can't select our free legal service.",
        'Where a new application is submitted before redemption of the current mortgage, the product can be ported.',
      ],
      waivedRefundedWithoutPorting: {
        id: 'portability-waived-refunded-without-porting',
        title: 'An ERC can also be waived/refunded without porting if:',
        listItems: [
          'a new mortgage is being taken in the last three months of the ERC period of the current product.',
          'you apply for a new application on a new product within three months of an existing BM BTL mortgage being redeemed.',
        ],
        paragraph: (
          <>
            For more information, please refer to our{' '}
            <Link href={routes.Porting}>porting guide.</Link>
          </>
        ),
      },
    },
    productTransfers: {
      id: 'product-transfers',
      title: 'Product transfers',
      boldParagraph:
        'Product transfers are available for Limited company mortgages at the end of the product period.',
      paragraph:
        "Once a product transfer's been submitted, an automated eligibility check will be carried out to make sure the mortgage qualifies. If the mortgage doesn't pass the eligibility check, you'll see an on-screen message explaining the reason why and what to do next.",
      reasonsEligibilityCheckMightFail: {
        id: 'product-transfers-reasons-eligibility-check-might-fail',
        title:
          'Reasons why the mortgage might fail the eligibility check include:',
        listItems: [
          'The account is a regulated mortgage.',
          'The account is in arrears.',
          'The account has multiple Direct Debits.',
          'The account has a payment arrangement in place.',
          'The tenure of the property information is missing from the account.',
          'No sub-accounts are available to transfer.',
          'The account requires a review of the sub-account balances.',
        ],
      },
    },
    productTransferTimescales: {
      id: 'product-transfer-timescales',
      title: 'Product transfer timescales',
      paragraphs: [
        'Product transfers can be submitted at any time in the month. The earliest a product transfer can start is the 1st of the following month.',
        "If the mortgage is within the last three months of an existing product, you can choose to start the new product early. If you select this option, we'll waive any early repayment charge (ERC). You can also choose to forward date the new product to start after the current product has ended.",
        'If the account has multiple sub-accounts with different ERC periods, you can only complete a product transfer application on the sub-account with the ERC expiring the soonest.',
        'When the mortgage is within the last 4 months of an existing product, you can choose to select a new product and forward date the new product to start after the current product has ended.',
      ],
      exampleKeyingDates: {
        id: 'product-transfer-timescales-example-keying-dates',
        title: 'Example keying dates',
        table: {
          headings: ['Product expiry', 'Keying from', 'Options for start date'],
          rows: [
            ['28 February', '01 November', '01 March'],
            ['', '01 December', '01 January or 01 March'],
            ['', '01 January', '01 February or 01 March'],
            ['', '01 February', '01 March'],
            ['31 March', '01 December', '01 April'],
            ['', '01 January', '01 February or 01 April'],
            ['', '01 February', '01 March or 01 April'],
            ['', '01 March', '01 April'],
          ],
        },
      },
    },
  },
};

export default limitedCompanyCriteria;
